import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  User, Users, CreditCard, Key, Plus, Copy, Trash2, 
  CheckCircle, Crown, Mail, Shield, Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tab } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [activeTab, setActiveTab] = useState(tab || "profile");
  const [team, setTeam] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Team modals
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Editor");
  
  // API Key modal
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  
  // Billing
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamRes, subRes, keysRes] = await Promise.all([
          axios.get(`${API}/teams`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/billing/subscription`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/api-keys`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setTeam(teamRes.data);
        setSubscription(subRes.data);
        setApiKeys(keysRes.data || []);
      } catch (error) {
        console.error('Error fetching settings data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Check payment status if returning from Stripe
  useEffect(() => {
    if (sessionId) {
      setCheckingPayment(true);
      const checkPayment = async () => {
        try {
          const response = await axios.get(`${API}/billing/status/${sessionId}`, { withCredentials: true });
          if (response.data.payment_status === 'paid') {
            toast.success("Payment successful! Your subscription is now active.");
            // Refresh subscription
            const subRes = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
            setSubscription(subRes.data);
          }
        } catch (error) {
          console.error('Error checking payment:', error);
        } finally {
          setCheckingPayment(false);
          // Clear session_id from URL
          navigate('/settings/billing', { replace: true });
        }
      };
      checkPayment();
    }
  }, [sessionId, navigate]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }
    try {
      await axios.post(`${API}/teams`, { name: newTeamName }, { withCredentials: true });
      toast.success("Team created successfully");
      setShowCreateTeam(false);
      setNewTeamName("");
      // Refresh team data
      const teamRes = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeam(teamRes.data);
    } catch (error) {
      toast.error("Failed to create team");
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email");
      return;
    }
    try {
      await axios.post(`${API}/teams/invite`, { 
        email: inviteEmail, 
        role: inviteRole 
      }, { withCredentials: true });
      toast.success("Member invited successfully");
      setShowInvite(false);
      setInviteEmail("");
      // Refresh team data
      const teamRes = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeam(teamRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite member");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      await axios.delete(`${API}/teams/members/${memberId}`, { withCredentials: true });
      toast.success("Member removed");
      const teamRes = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeam(teamRes.data);
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Please enter a key name");
      return;
    }
    try {
      const response = await axios.post(`${API}/api-keys`, { name: newKeyName }, { withCredentials: true });
      setCreatedKey(response.data);
      toast.success("API key created");
      const keysRes = await axios.get(`${API}/api-keys`, { withCredentials: true });
      setApiKeys(keysRes.data);
    } catch (error) {
      toast.error("Failed to create API key");
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to delete this API key?")) return;
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { withCredentials: true });
      toast.success("API key deleted");
      setApiKeys(keys => keys.filter(k => k.key_id !== keyId));
    } catch (error) {
      toast.error("Failed to delete API key");
    }
  };

  const [couponCode, setCouponCode] = useState("");
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const handleValidateCoupon = async (plan) => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/billing/validate-coupon`, {
        code: couponCode,
        plan
      }, { withCredentials: true });
      setCouponValidation(response.data);
      toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid coupon code");
      setCouponValidation(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        plan,
        origin_url: window.location.origin,
        coupon_code: couponValidation ? couponCode : null
      }, { withCredentials: true });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to start checkout");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); navigate(`/settings/${v}`); }}>
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="w-4 h-4 mr-2" /> Team
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">
              <CreditCard className="w-4 h-4 mr-2" /> Billing
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api">
              <Key className="w-4 h-4 mr-2" /> API
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6 mt-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading">Profile Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xl">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-foreground">{user?.name}</h3>
                    <p className="text-muted-foreground">{user?.email}</p>
                    <Badge className="mt-2" variant="outline">
                      <Shield className="w-3 h-3 mr-1" /> {user?.role}
                    </Badge>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input value={user?.name || ''} disabled className="bg-muted/50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input value={user?.email || ''} disabled className="bg-muted/50" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Profile information is managed through Google. Sign out and sign in again to refresh.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6 mt-6">
            {loading ? (
              <Card className="glass-card">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                </CardContent>
              </Card>
            ) : team ? (
              <Card className="glass-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">{team.name}</CardTitle>
                    <CardDescription>{team.members?.length || 0} members</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowInvite(true)}
                    className="rounded-full"
                    data-testid="invite-member-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Invite Member
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {team.members?.map((member) => (
                      <div 
                        key={member.user_id}
                        className="flex items-center justify-between p-4 rounded-xl bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={member.picture} />
                            <AvatarFallback className="bg-primary/10 text-primary text-sm">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground flex items-center gap-2">
                              {member.name}
                              {member.role === 'Admin' && team.owner_id === member.user_id && (
                                <Crown className="w-4 h-4 text-amber-500" />
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{member.role}</Badge>
                          {member.user_id !== user?.user_id && team.owner_id === user?.user_id && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-card">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-2">No Team Yet</h3>
                  <p className="text-muted-foreground mb-6">Create a team to collaborate with others</p>
                  <Button 
                    onClick={() => setShowCreateTeam(true)}
                    className="rounded-full"
                    data-testid="create-team-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Create Team
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6 mt-6">
            {checkingPayment && (
              <Card className="border-primary/50 bg-primary/5">
                <CardContent className="py-6 flex items-center justify-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-foreground">Verifying your payment...</span>
                </CardContent>
              </Card>
            )}

            {/* Current Plan */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading">Current Plan</CardTitle>
              </CardHeader>
              <CardContent>
                {subscription ? (
                  <div className="flex items-center justify-between p-4 rounded-xl bg-primary/10 border border-primary/20">
                    <div>
                      <p className="font-heading font-semibold text-foreground capitalize">
                        {subscription.plan} Plan
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {subscription.plan === 'starter' ? '5 workflows, 1,000 executions/mo' : 'Unlimited workflows, 10,000 executions/mo'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-green-500/10 text-green-500">Active</Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Renews {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'soon'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-muted/30 text-center">
                    <p className="text-muted-foreground">No active subscription</p>
                    <p className="text-sm text-muted-foreground">Choose a plan below to get started</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coupon Code */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Have a Coupon?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponValidation(null);
                    }}
                    placeholder="Enter coupon code"
                    className="font-mono"
                    data-testid="coupon-input"
                  />
                  <Button 
                    variant="outline"
                    onClick={() => handleValidateCoupon('starter')}
                    disabled={validatingCoupon || !couponCode.trim()}
                  >
                    {validatingCoupon ? "Validating..." : "Apply"}
                  </Button>
                </div>
                {couponValidation && (
                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">
                      ✓ {couponValidation.coupon.name} applied!
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {couponValidation.coupon.type === 'percentage' 
                        ? `${couponValidation.coupon.value}% off` 
                        : `$${couponValidation.coupon.value} off`} 
                      - You save ${couponValidation.discount_amount.toFixed(2)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Plans */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className={`glass-card ${subscription?.plan === 'starter' ? 'border-primary' : ''}`}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-1">Starter</h3>
                  <p className="text-muted-foreground text-sm mb-4">For individuals and small teams</p>
                  <div className="mb-4">
                    <span className="text-4xl font-heading font-bold text-foreground">$15</span>
                    <span className="text-muted-foreground">/user/month</span>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> 5 active workflows
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> 1,000 executions/month
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> Email support
                    </li>
                  </ul>
                  <Button 
                    className="w-full rounded-full"
                    variant={subscription?.plan === 'starter' ? 'outline' : 'default'}
                    onClick={() => handleSubscribe('starter')}
                    disabled={subscription?.plan === 'starter'}
                    data-testid="subscribe-starter-btn"
                  >
                    {subscription?.plan === 'starter' ? 'Current Plan' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>

              <Card className={`glass-card ${subscription?.plan === 'professional' ? 'border-primary' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xl font-heading font-semibold text-foreground">Professional</h3>
                    <Badge className="bg-primary/10 text-primary">Popular</Badge>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4">For growing businesses</p>
                  <div className="mb-4">
                    <span className="text-4xl font-heading font-bold text-foreground">$25</span>
                    <span className="text-muted-foreground">/user/month</span>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> Unlimited workflows
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> 10,000 executions/month
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> Priority support
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" /> Team collaboration
                    </li>
                  </ul>
                  <Button 
                    className="w-full rounded-full bg-primary hover:bg-primary/90"
                    onClick={() => handleSubscribe('professional')}
                    disabled={subscription?.plan === 'professional'}
                    data-testid="subscribe-professional-btn"
                  >
                    {subscription?.plan === 'professional' ? 'Current Plan' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* API Tab */}
          <TabsContent value="api" className="space-y-6 mt-6">
            <Card className="glass-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">API Keys</CardTitle>
                  <CardDescription>Manage your API keys for programmatic access</CardDescription>
                </div>
                <Button 
                  onClick={() => { setNewKeyName(""); setCreatedKey(null); setShowCreateKey(true); }}
                  className="rounded-full"
                  data-testid="create-api-key-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Key
                </Button>
              </CardHeader>
              <CardContent>
                {apiKeys.length > 0 ? (
                  <div className="space-y-3">
                    {apiKeys.map((apiKey) => (
                      <div 
                        key={apiKey.key_id}
                        className="flex items-center justify-between p-4 rounded-xl bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Key className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{apiKey.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Created {new Date(apiKey.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteApiKey(apiKey.key_id)}
                          className="text-red-500 hover:text-red-600"
                          data-testid={`delete-key-${apiKey.key_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No API keys created yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Team Modal */}
      <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Create Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Team Name</Label>
              <Input 
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="e.g., Marketing Team"
                data-testid="team-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTeam(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} data-testid="confirm-create-team-btn">Create Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Invite Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input 
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Editor">Editor</SelectItem>
                  <SelectItem value="Viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button onClick={handleInviteMember} data-testid="confirm-invite-btn">Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create API Key Modal */}
      <Dialog open={showCreateKey} onOpenChange={setShowCreateKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {createdKey ? 'API Key Created' : 'Create API Key'}
            </DialogTitle>
          </DialogHeader>
          {createdKey ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-600 mb-2">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Your API key has been created. Copy it now — you won't be able to see it again!
                </p>
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <Input 
                    value={createdKey.key}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(createdKey.key)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Key Name</Label>
                <Input 
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production API Key"
                  data-testid="api-key-name-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {createdKey ? (
              <Button onClick={() => setShowCreateKey(false)}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowCreateKey(false)}>Cancel</Button>
                <Button onClick={handleCreateApiKey} data-testid="confirm-create-api-key-btn">Create Key</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
